/**
 * 생성기 실행 결과를 화면 반응으로 바꾼다 — 토스트 · 입력창 위 안내 · 입력 비우기 · 사용량 갱신.
 * 실행하면 입력창은 이미 닫혀 있다. 성공·일부 성공은 토스트 하나와 함께 입력을 비우고, 중단은 ⓘ 토스트만
 * 남긴다. 나머지는 사용자가 다음 행동을 정해야 해 입력창을 다시 열어 안내 한 줄을 보인다.
 */
import { type AiGenerateDockRunCopy } from '../types/aiGenerateDock.types';

import { type AiGenerateDockUsage } from './aiGenerateDockOutcome';
import {
  type AiGenerateDockRunRejection,
  type AiGenerateDockRunResult,
} from './aiGenerateDockRunInput';

/** 실행 전에 막힌 이유 — 서버까지 가지 않았다. */
export type AiGenerateDockRunBlock =
  | 'unavailable'
  | 'busy'
  | 'no_slots'
  | 'no_session';

export type AiGenerateDockRunOutcome =
  | AiGenerateDockRunResult
  | { kind: 'blocked'; reason: AiGenerateDockRunBlock };

export type AiGenerateDockFeedback = {
  toast: string | null;
  /** 성공이 아닌 알림 토스트(ⓘ) — 중단. */
  infoToast: string | null;
  notice: {
    tone: 'neutral' | 'cautionary' | 'destructive';
    message: string;
    /** 같은 입력으로 다시 실행할 수 있다. */
    retry: boolean;
  } | null;
  /** 결과가 목록에 들어갔으니 입력을 비운다 — 다음은 새 요청이다. */
  resetInput: boolean;
  /** 생성이 통째로 실패했다 — 대기 카드를 실패 카드로 남긴다. 입력은 그대로 둔다. */
  failedCards: boolean;
  /** 응답에 실린 최신 생성기 사용량. 없으면 그대로 둔다. */
  usage: AiGenerateDockUsage | null;
  /** 무료 횟수를 다 썼다는 거절 — 응답에 사용량이 없어도 소진으로 표시한다. */
  quotaExhausted: boolean;
};

const NO_FEEDBACK: AiGenerateDockFeedback = {
  toast: null,
  infoToast: null,
  notice: null,
  resetInput: false,
  failedCards: false,
  usage: null,
  quotaExhausted: false,
};

export const resolveAiGenerateDockFeedback = (
  outcome: AiGenerateDockRunOutcome,
  copy: AiGenerateDockRunCopy,
): AiGenerateDockFeedback => {
  switch (outcome.kind) {
    case 'success':
      return {
        ...NO_FEEDBACK,
        toast: copy.success(outcome.created),
        resetInput: true,
        usage: outcome.usage,
      };
    case 'partial':
      return {
        ...NO_FEEDBACK,
        toast: copy.partial(outcome.created, outcome.failed),
        resetInput: true,
        usage: outcome.usage,
      };
    case 'failed':
      // 생성 자체 실패는 알럿·안내가 아니라 실패 카드로 보인다(PRD 3-5 · 5-6).
      return { ...NO_FEEDBACK, failedCards: true };
    case 'not_generated':
      return {
        ...NO_FEEDBACK,
        notice: {
          tone: 'destructive',
          message: copy.notGenerated,
          retry: false,
        },
      };
    case 'aborted':
      // 중단은 사용자가 고른 것이다 — 대기 카드만 걷고 입력은 남긴다(카드로 다시 열면 그대로).
      return { ...NO_FEEDBACK, infoToast: copy.cancelled };
    case 'blocked':
      return outcome.reason === 'busy'
        ? {
            ...NO_FEEDBACK,
            notice: {
              tone: 'cautionary',
              message: copy.chatBusy,
              retry: false,
            },
          }
        : outcome.reason === 'no_slots'
        ? NO_FEEDBACK
        : {
            ...NO_FEEDBACK,
            notice: {
              tone: 'destructive',
              message: copy.genericError,
              retry: false,
            },
          };
    case 'rejected':
      return resolveRejectionFeedback(outcome.rejection, copy);
  }
};

const resolveRejectionFeedback = (
  rejection: AiGenerateDockRunRejection,
  copy: AiGenerateDockRunCopy,
): AiGenerateDockFeedback => {
  switch (rejection.kind) {
    case 'quota_exceeded':
      return {
        ...NO_FEEDBACK,
        notice: {
          tone: 'cautionary',
          message: copy.quotaExceeded,
          retry: false,
        },
        usage: rejection.usage,
        quotaExhausted: true,
      };
    case 'rate_limited':
    case 'cooldown':
      return {
        ...NO_FEEDBACK,
        notice: {
          tone: 'cautionary',
          message:
            rejection.retryAfterSeconds !== null
              ? copy.retryAfter(rejection.retryAfterSeconds)
              : copy.tryAgainLater,
          retry: true,
        },
      };
    case 'session_locked':
      return {
        ...NO_FEEDBACK,
        notice: { tone: 'cautionary', message: copy.chatBusy, retry: true },
      };
    case 'auth_expired':
      return {
        ...NO_FEEDBACK,
        notice: {
          tone: 'destructive',
          message: copy.authExpired,
          retry: false,
        },
      };
    case 'invalid_request':
      return {
        ...NO_FEEDBACK,
        notice: {
          tone: 'destructive',
          message: copy.invalidRequest,
          retry: false,
        },
      };
    case 'other':
      return {
        ...NO_FEEDBACK,
        notice: {
          tone: 'destructive',
          message: copy.genericError,
          retry: true,
        },
      };
  }
};
